import type { CSSProperties } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CalendarEvent, CalendarIntegration } from '@cornerstone/shared';
import {
  ROOM_TIMETABLE_TOKENS as T,
  clipEvent,
  computeWindow,
  eventState,
  fmtHm,
  hourTicksEvery,
} from './roomTimetableLayout.js';

// =============================================================================
// RoomBoard — composite that aggregates every selected room. Renders at a
// fixed canvas pixel space so admin previews stay pixel-faithful with the
// Player's Canvas2D compositor. Wrap with <RoomBoardScaledBox> to fit the
// board into an arbitrary CSS container.
//
// Layouts (selected via `orientation`):
//   vertical (default) — time runs top-to-bottom, rooms are columns.
//   horizontal         — time on top, rooms as rows. Kept for legacy notices.
//
// All font / spacing constants mirror the formulas in
// player/src/media/roomStatusCompositor.ts so changing one means changing the
// other.
// =============================================================================

// Layout proportions. Mirrors player/src/media/roomStatusCompositor.ts so the
// admin preview stays pixel-faithful with the broadcast output.
const VERTICAL_TIME_COL_RATIO = 0.09;
const VERTICAL_HEADER_ROW_RATIO = 0.08;
const HORIZONTAL_LABEL_COL_RATIO = 0.18;

export type RoomBoardProps = {
  rooms: CalendarIntegration[];
  eventsByRoomId: Map<string, CalendarEvent[]>;
  nowMs: number;
  windowStartHour?: number;
  windowEndHour?: number;
  orientation?: 'horizontal' | 'vertical';
  /** Intrinsic canvas size — defaults to the single-wall 960×1080 footprint. */
  canvasWidth?: number;
  canvasHeight?: number;
};

export function RoomBoard({
  rooms,
  eventsByRoomId,
  nowMs,
  windowStartHour = 8,
  windowEndHour = 20,
  orientation = 'vertical',
  canvasWidth = 960,
  canvasHeight = 1080,
}: RoomBoardProps): JSX.Element {
  const win = useMemo(
    () => computeWindow(nowMs, windowStartHour, windowEndHour),
    [nowMs, windowStartHour, windowEndHour],
  );

  const containerStyle: CSSProperties = {
    position: 'relative',
    width: canvasWidth,
    height: canvasHeight,
    backgroundColor: T.bgPanel,
    color: T.text,
    fontFamily: T.fontUi,
    overflow: 'hidden',
  };

  if (rooms.length === 0) {
    return (
      <div style={containerStyle}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: T.fontMono,
            fontSize: Math.floor(canvasHeight * 0.04),
            color: T.textDimmer,
          }}
        >
          등록된 회의실이 없습니다.
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {orientation === 'vertical' ? (
        <VerticalBoard
          rooms={rooms}
          eventsByRoomId={eventsByRoomId}
          nowMs={nowMs}
          win={win}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
        />
      ) : (
        <HorizontalBoard
          rooms={rooms}
          eventsByRoomId={eventsByRoomId}
          nowMs={nowMs}
          win={win}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// VERTICAL — time axis vertical, rooms as columns.
// -----------------------------------------------------------------------------
function VerticalBoard({
  rooms,
  eventsByRoomId,
  nowMs,
  win,
  canvasWidth: w,
  canvasHeight: h,
}: {
  rooms: CalendarIntegration[];
  eventsByRoomId: Map<string, CalendarEvent[]>;
  nowMs: number;
  win: ReturnType<typeof computeWindow>;
  canvasWidth: number;
  canvasHeight: number;
}): JSX.Element {
  const timeColW = Math.floor(w * VERTICAL_TIME_COL_RATIO);
  const headerH = Math.floor(h * VERTICAL_HEADER_ROW_RATIO);
  const bodyX = timeColW;
  const bodyY = headerH;
  const bodyW = w - timeColW;
  const bodyH = h - headerH;
  const colW = bodyW / rooms.length;

  const roomLabelSize = Math.max(14, Math.min(Math.floor(headerH * 0.30), 28));
  const roomMetaSize = Math.max(10, Math.floor(roomLabelSize * 0.55));
  const axisFontSize = Math.max(11, Math.min(Math.floor(timeColW * 0.18), 20));

  const ticks = useMemo(() => hourTicksEvery(win, 1), [win]);

  return (
    <>
      {/* Time column background */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: timeColW,
          height: h,
          backgroundColor: T.bgPanel2,
          borderRight: `1px solid ${T.line}`,
        }}
      />
      {/* Header strip background across rooms */}
      <div
        style={{
          position: 'absolute',
          left: bodyX,
          top: 0,
          width: bodyW,
          height: headerH,
          backgroundColor: T.bgPanel2,
          borderBottom: `1px solid ${T.line}`,
        }}
      />
      {/* Room headers */}
      {rooms.map((room, idx) => {
        const cx = bodyX + colW * idx;
        const events = eventsByRoomId.get(room.id) ?? [];
        return (
          <div
            key={`hdr-${room.id}`}
            style={{
              position: 'absolute',
              left: cx,
              top: 0,
              width: colW,
              height: headerH,
              borderRight: `1px solid ${T.line}`,
              padding: `${Math.floor(headerH * 0.1)}px ${Math.floor(colW * 0.04)}px`,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <div
              title={room.label}
              style={{
                fontFamily: T.fontUi,
                fontWeight: 500,
                fontSize: roomLabelSize,
                color: T.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.1,
              }}
            >
              {cleanLabel(room.label)}
            </div>
            <div
              style={{
                fontFamily: T.fontMono,
                fontSize: roomMetaSize,
                color: room.lastError ? T.red : T.textDim,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginTop: 2,
              }}
            >
              {events.length} ev
              {room.lastPolledAt
                ? ` · ${room.lastError ? '⚠' : '●'} ${formatAgo(nowMs - Date.parse(room.lastPolledAt))}`
                : ''}
            </div>
          </div>
        );
      })}

      {/* Time axis labels */}
      {ticks.map((t) => {
        const ty = bodyY + ((t - win.startMs) / win.durationMs) * bodyH;
        return (
          <span
            key={`tick-${t}`}
            style={{
              position: 'absolute',
              right: w - timeColW + 6,
              top: ty,
              transform: 'translateY(-50%)',
              fontFamily: T.fontMono,
              fontSize: axisFontSize,
              color: T.textDim,
              whiteSpace: 'nowrap',
            }}
          >
            {fmtHm(t)}
          </span>
        );
      })}

      {/* Room column body + grid + events */}
      {rooms.map((room, idx) => {
        const cx = bodyX + colW * idx;
        const events = eventsByRoomId.get(room.id) ?? [];
        const evTitleSize = Math.max(11, Math.min(Math.floor(colW * 0.06), 18));
        const evTimeSize = Math.max(9, Math.floor(evTitleSize * 0.7));
        return (
          <div
            key={`col-${room.id}`}
            style={{
              position: 'absolute',
              left: cx,
              top: bodyY,
              width: colW,
              height: bodyH,
              backgroundColor: T.bgPanel3,
              borderRight: `1px solid ${T.line}`,
              overflow: 'hidden',
            }}
          >
            {/* horizontal hour gridlines */}
            {ticks.map((t) => {
              const ty = ((t - win.startMs) / win.durationMs) * bodyH;
              return (
                <div
                  key={`g-${t}`}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: ty,
                    height: 1,
                    backgroundColor: T.line,
                    opacity: 0.6,
                  }}
                />
              );
            })}
            {/* events */}
            {events.map((ev) => {
              const clipped = clipEvent(ev, win);
              if (!clipped) return null;
              const top = ((clipped.startMs - win.startMs) / win.durationMs) * bodyH;
              const height = Math.max(
                14,
                ((clipped.endMs - clipped.startMs) / win.durationMs) * bodyH,
              );
              const state = eventState(ev, nowMs);
              const isCancelled = ev.status === 'cancelled';
              const isTentative = ev.status === 'tentative';
              const bg = isCancelled
                ? 'transparent'
                : state === 'past'
                  ? 'rgba(138,141,147,0.18)'
                  : T.accentSoft;
              const border = isCancelled
                ? `1px dashed ${T.textDimmer}`
                : isTentative
                  ? `1px dashed ${T.yellow}`
                  : `1px solid ${T.accentRing}`;
              const color = isCancelled || state === 'past' ? T.textDim : T.text;
              const pad = Math.max(4, Math.floor(colW * 0.04));
              return (
                <div
                  key={ev.id}
                  title={`${fmtHm(ev.startAt)}–${fmtHm(ev.endAt)} ${ev.summary || '(no title)'}`}
                  style={{
                    position: 'absolute',
                    left: pad,
                    right: pad,
                    top,
                    height,
                    backgroundColor: bg,
                    border,
                    borderRadius: 2,
                    padding: `${pad}px ${pad}px`,
                    boxSizing: 'border-box',
                    color,
                    textDecoration: isCancelled ? 'line-through' : undefined,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <div
                    style={{
                      fontFamily: T.fontMono,
                      fontSize: evTimeSize,
                      color: T.textDim,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fmtHm(ev.startAt)}–{fmtHm(ev.endAt)}
                  </div>
                  <div
                    style={{
                      fontFamily: T.fontUi,
                      fontSize: evTitleSize,
                      fontWeight: 500,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {ev.summary || '(no title)'}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* NOW horizontal line + chip */}
      {nowMs >= win.startMs && nowMs <= win.endMs && (
        <>
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: bodyY + ((nowMs - win.startMs) / win.durationMs) * bodyH,
              width: w,
              height: 2,
              backgroundColor: T.red,
              boxShadow: `0 0 4px ${T.red}`,
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: 4,
              top: bodyY + ((nowMs - win.startMs) / win.durationMs) * bodyH,
              transform: 'translateY(-50%)',
              width: Math.max(timeColW - 8, 44),
              height: Math.max(20, Math.floor(axisFontSize * 1.4)),
              backgroundColor: T.red,
              color: '#0a0b0d',
              fontFamily: T.fontMono,
              fontWeight: 700,
              fontSize: Math.floor(axisFontSize * 0.7),
              letterSpacing: '0.08em',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            NOW
          </div>
        </>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// HORIZONTAL — legacy layout (time on top, rooms as rows).
// -----------------------------------------------------------------------------
function HorizontalBoard({
  rooms,
  eventsByRoomId,
  nowMs,
  win,
  canvasWidth: w,
  canvasHeight: h,
}: {
  rooms: CalendarIntegration[];
  eventsByRoomId: Map<string, CalendarEvent[]>;
  nowMs: number;
  win: ReturnType<typeof computeWindow>;
  canvasWidth: number;
  canvasHeight: number;
}): JSX.Element {
  const axisH = Math.max(48, Math.floor(h * 0.07));
  const labelColW = Math.floor(w * HORIZONTAL_LABEL_COL_RATIO);
  const stripX = labelColW;
  const stripW = w - labelColW;
  const rowH = (h - axisH) / rooms.length;
  const ticks = useMemo(() => hourTicksEvery(win, 1), [win]);
  const axisFontSize = Math.max(11, Math.min(Math.floor(axisH * 0.32), 22));

  return (
    <>
      {/* Axis */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: w,
          height: axisH,
          backgroundColor: T.bgPanel2,
          borderBottom: `1px solid ${T.line}`,
        }}
      />
      {ticks.map((t) => {
        const tx = stripX + ((t - win.startMs) / win.durationMs) * stripW;
        return (
          <span
            key={`hat-${t}`}
            style={{
              position: 'absolute',
              left: tx,
              top: axisH / 2,
              transform: 'translate(-50%, -50%)',
              fontFamily: T.fontMono,
              fontSize: axisFontSize,
              color: T.textDim,
              whiteSpace: 'nowrap',
            }}
          >
            {fmtHm(t)}
          </span>
        );
      })}
      {/* Rows */}
      {rooms.map((room, idx) => {
        const rowY = axisH + rowH * idx;
        const events = eventsByRoomId.get(room.id) ?? [];
        const labelSize = Math.max(14, Math.min(Math.floor(rowH * 0.24), 30));
        const metaSize = Math.max(10, Math.floor(labelSize * 0.55));
        const evFontSize = Math.max(11, Math.min(Math.floor(rowH * 0.22), 22));
        const evPad = Math.max(4, Math.floor(rowH * 0.08));
        return (
          <div
            key={`hr-${room.id}`}
            style={{
              position: 'absolute',
              left: 0,
              top: rowY,
              width: w,
              height: rowH,
              borderBottom: `1px solid ${T.line}`,
              display: 'flex',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                width: labelColW,
                padding: `0 ${Math.max(12, Math.floor(rowH * 0.18))}px`,
                borderRight: `1px solid ${T.line}`,
                backgroundColor: T.bgPanel2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  fontFamily: T.fontUi,
                  fontWeight: 500,
                  fontSize: labelSize,
                  color: T.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {cleanLabel(room.label)}
              </div>
              <div
                style={{
                  fontFamily: T.fontMono,
                  fontSize: metaSize,
                  color: room.lastError ? T.red : T.textDim,
                  whiteSpace: 'nowrap',
                }}
              >
                {events.length} ev
                {room.lastPolledAt
                  ? `  ·  ${room.lastError ? '⚠' : '●'} ${formatAgo(nowMs - Date.parse(room.lastPolledAt))}`
                  : ''}
              </div>
            </div>
            <div
              style={{
                position: 'relative',
                flex: 1,
                backgroundColor: T.bgPanel3,
                overflow: 'hidden',
              }}
            >
              {ticks.map((t) => {
                const tx = ((t - win.startMs) / win.durationMs) * stripW;
                return (
                  <div
                    key={`hg-${t}`}
                    style={{
                      position: 'absolute',
                      left: tx,
                      top: 0,
                      bottom: 0,
                      width: 1,
                      backgroundColor: T.line,
                      opacity: 0.6,
                    }}
                  />
                );
              })}
              {events.map((ev) => {
                const clipped = clipEvent(ev, win);
                if (!clipped) return null;
                const left = ((clipped.startMs - win.startMs) / win.durationMs) * stripW;
                const width = Math.max(
                  8,
                  ((clipped.endMs - clipped.startMs) / win.durationMs) * stripW,
                );
                const state = eventState(ev, nowMs);
                const isCancelled = ev.status === 'cancelled';
                const isTentative = ev.status === 'tentative';
                const bg = isCancelled
                  ? 'transparent'
                  : state === 'past'
                    ? 'rgba(138,141,147,0.18)'
                    : T.accentSoft;
                const border = isCancelled
                  ? `1px dashed ${T.textDimmer}`
                  : isTentative
                    ? `1px dashed ${T.yellow}`
                    : `1px solid ${T.accentRing}`;
                const color = isCancelled || state === 'past' ? T.textDim : T.text;
                return (
                  <div
                    key={ev.id}
                    title={`${fmtHm(ev.startAt)}–${fmtHm(ev.endAt)} ${ev.summary || '(no title)'}`}
                    style={{
                      position: 'absolute',
                      left,
                      width,
                      top: evPad,
                      bottom: evPad,
                      backgroundColor: bg,
                      border,
                      borderRadius: 2,
                      padding: `${Math.max(2, evPad / 2)}px ${Math.max(4, evPad)}px`,
                      boxSizing: 'border-box',
                      fontFamily: T.fontUi,
                      fontWeight: 500,
                      fontSize: evFontSize,
                      color,
                      textDecoration: isCancelled ? 'line-through' : undefined,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {ev.summary || '(no title)'}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {nowMs >= win.startMs && nowMs <= win.endMs && (
        <>
          <div
            style={{
              position: 'absolute',
              left: stripX + ((nowMs - win.startMs) / win.durationMs) * stripW,
              top: 0,
              bottom: 0,
              width: 2,
              backgroundColor: T.red,
              boxShadow: `0 0 4px ${T.red}`,
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: stripX + ((nowMs - win.startMs) / win.durationMs) * stripW,
              top: 0,
              transform: 'translateX(-50%)',
              padding: `${Math.max(2, Math.floor(axisH * 0.1))}px ${Math.max(4, Math.floor(axisH * 0.18))}px`,
              backgroundColor: T.red,
              color: '#0a0b0d',
              fontFamily: T.fontMono,
              fontWeight: 700,
              fontSize: Math.floor(axisFontSize * 0.8),
              letterSpacing: '0.08em',
              borderRadius: 2,
              pointerEvents: 'none',
            }}
          >
            NOW
          </div>
        </>
      )}
    </>
  );
}

export function RoomBoardScaledBox(
  props: RoomBoardProps & { className?: string; style?: CSSProperties },
): JSX.Element {
  const cw = props.canvasWidth ?? 960;
  const ch = props.canvasHeight ?? 1080;
  const { className, style, ...rest } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.3);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setScale(Math.min(width / cw, height / ch));
        }
        break;
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [cw, ch]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      <div
        style={{
          width: cw,
          height: ch,
          transformOrigin: 'center center',
          transform: `scale(${scale})`,
          flexShrink: 0,
        }}
      >
        <RoomBoard {...rest} canvasWidth={cw} canvasHeight={ch} />
      </div>
    </div>
  );
}

function formatAgo(ms: number): string {
  if (ms < 0) return 'now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

// Strip a trailing parenthesized integer (e.g. capacity tag like "Tokyo (10)")
// from the display label. The underlying CalendarIntegration.label stays
// untouched so the Integrations admin still sees what was originally typed.
function cleanLabel(label: string): string {
  return label.replace(/\s*\(\d+\)\s*$/, '');
}
